/**
 * Addresses a person types for a machine on the branch network: the Branch Hub (M7-4) and the
 * network printers (M7-6). Pure rules, the same on the server, a PC and (M8) a phone.
 */

/**
 * Arabic-Indic (٠–٩) and Persian (۰–۹) digits → Latin, and the Arabic decimal separator (٫) →
 * ".". An Arabic keyboard types them; the person must never be told a correct address is wrong.
 */
export function latinDigits(text: string): string {
  return text
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/٫/g, '.');
}

/** Invisible marks a keyboard or a copy from a document can add (bidi marks, zero widths). */
const INVISIBLE = new RegExp(
  `[${(
    [
      [0x200b, 0x200f], // zero widths, left-to-right and right-to-left marks
      [0x202a, 0x202e], // bidi embeddings and overrides
      [0x2066, 0x2069], // bidi isolates
      [0xfeff, 0xfeff], // byte order mark
    ] as const
  )
    .map(([from, to]) => `${String.fromCharCode(from)}-${String.fromCharCode(to)}`)
    .join('')}]`,
  'g',
);

/** Removes the invisible marks above (the D115 lesson: a copied "YES" was not "YES"). */
export function stripInvisible(text: string): string {
  return text.replace(INVISIBLE, '');
}

export interface LanAddress {
  readonly host: string;
  /** Null when not typed: the caller's default applies. */
  readonly port: number | null;
}

/**
 * A LAN address as typed: `192.168.1.10`, `192.168.1.10:8443`, or a PC name (`HUB-PC`,
 * `hub-pc.local`). IPv4 only (D129). Returns null when it cannot be an address.
 */
export function parseLanAddress(text: string): LanAddress | null {
  const value = stripInvisible(latinDigits(text)).trim().toLowerCase();
  const match = /^([^:\s]+)(?::(\d{1,5}))?$/.exec(value);
  if (!match) return null;
  const host = match[1] ?? '';
  const port = match[2] === undefined ? null : Number(match[2]);
  if (port !== null && (port < 1 || port > 65_535)) return null;
  if (/^\d+(\.\d+)*$/.test(host)) {
    const parts = host.split('.');
    if (parts.length !== 4 || parts.some((p) => p.length > 3 || Number(p) > 255)) return null;
    return { host: parts.map(Number).join('.'), port };
  }
  const labels = host.split('.');
  const label = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
  if (host.length > 253 || labels.some((l) => !label.test(l))) return null;
  return { host, port };
}
